/** Minimal translate function, so the helper works with any Lingui `i18n`. */
interface CoverageI18n {
  _: (descriptor: { id: string; message: string; values?: Record<string, unknown> }) => string;
}

function readString(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readNumber(payload: Record<string, unknown>, key: string): number | undefined {
  const value = payload[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** The last entry of samples[] (appended in time order) that carries the staff counts. */
function newestSample(payload: Record<string, unknown>): Record<string, unknown> | undefined {
  const samples = payload.samples;
  if (!Array.isArray(samples)) return undefined;
  for (let i = samples.length - 1; i >= 0; i -= 1) {
    const sample: unknown = samples[i];
    if (sample && typeof sample === 'object' && readNumber(sample as Record<string, unknown>, 'required_staff') !== undefined) {
      return sample as Record<string, unknown>;
    }
  }
  return undefined;
}

/**
 * HH:mm of an org-local RFC 3339 string. The clock time is read from the text so
 * the browser zone cannot shift it; the locale only picks the digits.
 */
function clockTime(value: string | undefined, locale: string): string | undefined {
  if (!value || value.length < 16 || value[10] !== 'T') return undefined;
  const hours = Number(value.slice(11, 13));
  const minutes = Number(value.slice(14, 16));
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return undefined;
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      // h23, not hour12: false: some engines render midnight as "24:05" with the latter.
      hourCycle: 'h23',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(2000, 0, 1, hours, minutes)));
  } catch {
    return value.slice(11, 16);
  }
}

/**
 * The inbox and board line of a coverage_breach item:
 * "{group} · {inGroup} of {required} in the group since {time}". Parts the payload
 * does not carry are left out. Counts only; the payload names no child.
 */
export function coverageBreachSummary(
  i18n: CoverageI18n,
  payload: Record<string, unknown> | undefined,
  locale: string,
): string | undefined {
  if (!payload) return undefined;
  const group = readString(payload, 'group_name');
  // The top-level counts are those of the run that raised the item; the newest
  // sample is the current state while the breach goes on.
  const counts = newestSample(payload) ?? payload;
  const required = readNumber(counts, 'required_staff');
  const inGroup = readNumber(counts, 'in_group_staff') ?? 0;
  const since = clockTime(readString(payload, 'started_at'), locale);

  let detail: string | undefined;
  if (required !== undefined && since) {
    detail = i18n._({
      id: 'workflow.coverageBreach.summaryCountSince',
      message: '{inGroup} of {required} in the group since {time}',
      values: { inGroup, required, time: since },
    });
  } else if (required !== undefined) {
    detail = i18n._({
      id: 'workflow.coverageBreach.summaryCount',
      message: '{inGroup} of {required} in the group',
      values: { inGroup, required },
    });
  } else if (since) {
    detail = i18n._({
      id: 'workflow.coverageBreach.summarySince',
      message: 'since {time}',
      values: { time: since },
    });
  }
  const parts = [group, detail].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}
